import { useEffect, useRef, useState } from 'react';
import { CalendarDays, ICON_STROKE_WIDTH, Menu, X } from './Icon';
import { assets } from '../content';
import { RollingButton } from './RollingButton';

type SiteNavProps = { onReserve: () => void };

const navLinks = [
  ['Why attend', '#why-attend'],
  ['Programme', '#programme'],
  ['Speakers', '#speakers'],
  ['RSVP', '#rsvp'],
  ['FAQ', '#faq'],
] as const;

export function SiteNav({ onReserve }: SiteNavProps) {
  const [open, setOpen] = useState(false);
  const closeButton = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        return;
      }
      if (event.key !== 'Tab' || !menuRef.current) return;
      const focusable = Array.from(menuRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), a[href]'));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    requestAnimationFrame(() => closeButton.current?.focus());
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const reserve = () => {
    setOpen(false);
    onReserve();
  };

  return (
    <>
      <header className="relative z-30 mx-auto w-full max-w-[1440px] p-2 sm:p-3" data-od-id="site-navigation">
        <nav className="flex min-h-14 items-center justify-between rounded-full bg-white p-[5px] shadow-[0_10px_35px_rgba(17,24,39,.08)]" aria-label="Primary navigation">
          <div className="flex min-w-0 items-center gap-7">
            <a href="#top" className="flex h-11 items-center rounded-full px-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]" aria-label="AI Ready Tobago home">
              <img src={assets.logo} alt="Tobago Information Technology Limited" className="h-10 w-auto max-w-[200px] object-contain sm:h-11" />
            </a>
            <div className="hidden items-center gap-6 md:flex">
              {navLinks.map(([label, href]) => (
                <a key={href} href={href} className="text-sm font-medium text-[var(--fg)] transition-colors duration-300 hover:text-[var(--muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]">
                  {label}
                </a>
              ))}
            </div>
          </div>

          <div className="hidden items-center gap-4 md:flex">
            <div className="hidden items-center gap-2 text-[13px] font-medium text-[var(--muted)] lg:flex">
              <CalendarDays className="h-3.5 w-3.5" strokeWidth={ICON_STROKE_WIDTH} aria-hidden="true" />
              <span>13 August 2026 · Scarborough</span>
            </div>
            <RollingButton variant="dark" onClick={onReserve} dataOdId="nav-reserve-button">Reserve your free seat</RollingButton>
          </div>

          <button
            type="button"
            onClick={() => setOpen(true)}
            className="flex min-h-11 items-center gap-2 rounded-full bg-[var(--fg)] px-4 text-[13px] font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] focus-visible:ring-offset-2 md:hidden"
            aria-expanded={open}
            aria-controls="mobile-menu"
            data-od-id="mobile-menu-button"
          >
            <Menu className="h-4 w-4" strokeWidth={ICON_STROKE_WIDTH} aria-hidden="true" /> Menu
          </button>
        </nav>
      </header>

      <div className={`fixed inset-0 z-50 transition-visibility duration-500 ${open ? 'visible' : 'invisible'}`} aria-hidden={!open}>
        <button className={`absolute inset-0 bg-black/60 transition-opacity duration-500 ${open ? 'opacity-100' : 'opacity-0'}`} onClick={() => setOpen(false)} aria-label="Close menu backdrop" tabIndex={open ? 0 : -1} />
        <div
          ref={menuRef}
          id="mobile-menu"
          role="dialog"
          aria-modal="true"
          aria-label="Mobile navigation"
          className={`absolute inset-x-3 bottom-3 rounded-2xl bg-white p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] shadow-2xl transition-transform duration-500 ease-[cubic-bezier(0.32,0.72,0,1)] ${open ? 'translate-y-0' : 'translate-y-[calc(100%+1rem)]'}`}
        >
          <div className="mb-8 flex items-center justify-between">
            <div className="inline-flex items-center gap-2 rounded-full bg-[var(--bg)] px-3 py-2 text-xs font-semibold text-[var(--muted)]">
              <CalendarDays className="h-3.5 w-3.5" strokeWidth={ICON_STROKE_WIDTH} aria-hidden="true" /> Thursday, 13 August
            </div>
            <button ref={closeButton} type="button" onClick={() => setOpen(false)} className="grid h-11 w-11 place-items-center rounded-full bg-[var(--fg)] text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]" aria-label="Close menu">
              <X className="h-5 w-5" strokeWidth={ICON_STROKE_WIDTH} aria-hidden="true" />
            </button>
          </div>
          <div className="mb-8 flex flex-col">
            {navLinks.map(([label, href]) => (
              <a key={href} href={href} onClick={() => setOpen(false)} className="border-b border-[var(--border)] py-4 text-[28px] font-medium leading-8 tracking-[-0.03em] text-[var(--fg)]">
                {label}
              </a>
            ))}
          </div>
          <RollingButton onClick={reserve} className="w-full justify-between" dataOdId="mobile-reserve-button">Reserve your free seat</RollingButton>
        </div>
      </div>
    </>
  );
}
